// SUBSCRIBE, ported from internal/wire/subscribe.go.
//
// The add/remove chunk diff a client sends on pan (SPEC §3, "clients subscribe to
// visible chunks plus a one-chunk margin and send a subscribe/unsubscribe diff on
// pan"). A diff rather than a full set, because a pan of one chunk otherwise
// resends the whole viewport every frame of a drag.
//
//	u16 addCount
//	addCount x u16 chunkId
//	u16 removeCount
//	removeCount x u16 chunkId
//
// The header tick is the last tick the client applied. The server gets a
// staleness read for free and still keeps no per-connection replication state —
// which is also why an EMPTY subscribe is the client heartbeat: 13 bytes that
// resets the server's read deadline and reports where the client has got to.

import { Reader, Writer } from './bytes';
import { ClassSubscribe } from './classes';
import { NumChunks, validChunk, type ChunkID, type TickNo } from './core';
import { errChunkID, errDuplicateChunk, errTooManyRecords, errTruncated } from './errors';

export interface SubscribeDiff {
  /** The last tick the client applied. */
  tick: TickNo;
  add: ChunkID[];
  remove: ChunkID[];
}

export function encodeSubscribe(d: {
  tick: TickNo;
  add?: readonly ChunkID[];
  remove?: readonly ChunkID[];
}): Uint8Array {
  const add = d.add ?? [];
  const remove = d.remove ?? [];
  if (add.length > NumChunks || remove.length > NumChunks) throw errTooManyRecords();
  const seen = new Set<number>();
  for (const c of add) {
    if (!validChunk(c)) throw errChunkID();
    seen.add(c);
  }
  for (const c of remove) {
    if (!validChunk(c)) throw errChunkID();
    // Not a pan: it is a client bug or a probe, and the server refuses it too.
    if (seen.has(c)) throw errDuplicateChunk();
  }
  const w = new Writer(9 + 4 + 2 * (add.length + remove.length));
  w.header(ClassSubscribe, d.tick).u16(add.length);
  for (const c of add) w.u16(c);
  w.u16(remove.length);
  for (const c of remove) w.u16(c);
  return w.finish();
}

/** The client never receives one; the decoder exists to prove the encoder against. */
export function decodeSubscribe(b: Uint8Array): SubscribeDiff {
  const r = new Reader(b);
  const tick = r.header(ClassSubscribe);
  const add = readChunkList(r);
  const remove = readChunkList(r);
  r.done();
  const seen = new Set(add);
  for (const c of remove) if (seen.has(c)) throw errDuplicateChunk();
  return { tick, add, remove };
}

function readChunkList(r: Reader): ChunkID[] {
  const n = r.u16();
  if (n > NumChunks) throw errTooManyRecords();
  if (n * 2 > r.remaining) throw errTruncated();
  const list: ChunkID[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const c = r.u16();
    if (!validChunk(c)) throw errChunkID();
    list[i] = c;
  }
  return list;
}
